import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PlanCard } from './shared/PlanCard';
import { pricingPlans, PricingPlan } from '@/data/pricingPlans';

interface PricingPlansProps {
  showContactSales?: boolean;
  className?: string;
}

export const PricingPlans: React.FC<PricingPlansProps> = ({ 
  showContactSales = true, 
  className = '' 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handlePlanSelect = (plan: PricingPlan) => {
    // Enterprise plan goes to contact page
    if (plan.id === 'enterprise') {
      navigate('/contact');
      return;
    }
    
    // Advanced plan goes to advanced signup (no trial)
    if (plan.id === 'advanced') {
      navigate('/advanced-signup');
      return;
    }
    
    // Essential plan redirects to business trial
    navigate('/business-trial', { 
      state: { 
        selectedPlan: plan.id,
        planName: plan.name,
        planPrice: plan.price
      } 
    });
  };

  // Use fixed grid layout for 3 plans
  const gridCols = 'md:grid-cols-3 max-w-5xl';
  
  return (
    <section className={`py-16 ${className}`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Choose Your Perfect Plan
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Start with a free trial and scale as you grow. All plans include our core features.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto items-stretch">
          {pricingPlans.map((plan, index) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              variant="public"
              index={index}
              onSelectPlan={handlePlanSelect}
            />
          ))}
        </div>
      </div>
    </section>
  );
};